import React, { useState } from 'react';

interface DeleteAccountDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => Promise<void>;
  isDeleting: boolean;
}

const DeleteAccountDialog: React.FC<DeleteAccountDialogProps> = ({
  isOpen,
  onClose,
  onConfirm,
  isDeleting,
}) => {
  const [confirmText, setConfirmText] = useState('');
  const CONFIRM_PHRASE = 'DELETE';

  const handleClose = () => {
    if (!isDeleting) {
      setConfirmText('');
      onClose();
    }
  };

  const handleConfirm = async () => {
    if (confirmText !== CONFIRM_PHRASE) return;
    await onConfirm();
    setConfirmText('');
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="delete-account-title"
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={handleClose}
      />

      {/* Dialog */}
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md p-6 z-10">
        {/* Warning icon */}
        <div className="flex items-center justify-center w-14 h-14 rounded-full bg-red-100 mx-auto mb-4">
          <svg
            className="w-7 h-7 text-red-600"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z"
            />
          </svg>
        </div>

        <h2
          id="delete-account-title"
          className="text-xl font-heading font-bold text-center text-secondary-dark mb-2"
        >
          Delete your account?
        </h2>

        <p className="text-secondary text-center text-sm mb-4">
          This action is <strong className="text-red-600">permanent and irreversible</strong>.
          All of your data will be deleted, including:
        </p>

        <ul className="bg-red-50 border border-red-100 rounded-lg px-4 py-3 mb-5 space-y-1 text-sm text-red-700">
          <li className="flex items-center gap-2">
            <span className="text-red-400">✕</span>
            Your account and login credentials
          </li>
          <li className="flex items-center gap-2">
            <span className="text-red-400">✕</span>
            All your personal movies
          </li>
          <li className="flex items-center gap-2">
            <span className="text-red-400">✕</span>
            All your favorites &amp; watchlist
          </li>
        </ul>

        <div className="mb-5">
          <label
            htmlFor="confirm-delete-input"
            className="block text-sm font-medium text-secondary-dark mb-1"
          >
            Type <span className="font-mono font-bold text-red-600">{CONFIRM_PHRASE}</span> to confirm
          </label>
          <input
            id="confirm-delete-input"
            type="text"
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            placeholder={CONFIRM_PHRASE}
            disabled={isDeleting}
            className="w-full border border-secondary/20 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-400 focus:border-transparent disabled:opacity-50"
            autoComplete="off"
          />
        </div>

        <div className="flex gap-3">
          <button
            type="button"
            onClick={handleClose}
            disabled={isDeleting}
            className="flex-1 px-4 py-2 rounded-lg border border-secondary/20 text-secondary-dark font-medium text-sm hover:bg-secondary/5 transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={confirmText !== CONFIRM_PHRASE || isDeleting}
            className="flex-1 px-4 py-2 rounded-lg bg-red-600 text-white font-medium text-sm hover:bg-red-700 transition-colors disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {isDeleting ? (
              <>
                <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                </svg>
                Deleting…
              </>
            ) : (
              'Delete my account'
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default DeleteAccountDialog;
